/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { Fragment } from 'react'
import { ComboboxOption } from '@headlessui/react'
import clsx from 'clsx'
import { CheckIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { CompanyContact } from 'types/entities/CompanyContact'
import { getCompanyContactDisplayName } from 'utils/companiesUtils'
import { isGenericCompanyContact } from 'utils/contacts/genericContactUtils'

interface CompanyContactAutocompleteItemsListProps {
    contacts: CompanyContact[]
    selectedOptionRef: React.RefObject<HTMLLIElement>
}

const CompanyContactAutocompleteItemsList = (props: CompanyContactAutocompleteItemsListProps) => {
    const { t } = useTranslation()

    if (!props.contacts.length) {
        return null
    }

    return (
        <>
            {props.contacts.map((contact) => (
                <ComboboxOption key={contact.id} value={contact} as={Fragment}>
                    {({ selected, active }) => (
                        <li
                            ref={selected ? props.selectedOptionRef : null}
                            className={clsx(
                                'relative flex flex-row cursor-pointer select-none py-1.5 pr-0 text-sm text-gray-900 dark:text-gray-100',
                                active || selected
                                    ? 'bg-gray-200/80 text-black dark:bg-gray-800 dark:text-gray-100'
                                    : ''
                            )}
                        >
                            {selected && (
                                <span className={clsx('w-9 flex items-center')}>
                                    <CheckIcon className={clsx('w-[18px] h-[18px] grow ml-0')} />
                                </span>
                            )}
                            <div className={clsx('flex flex-col', !selected && 'pl-9')}>
                                <span className={clsx('block truncate font-medium')}>
                                    {`${contact.id} • ${getCompanyContactDisplayName(contact) || [contact.first_name, contact.last_name].filter(Boolean).join(' ')}`}
                                    {isGenericCompanyContact(contact) ? ` · ${t('Generic contact')}` : ''}
                                </span>
                                <span className={clsx('block truncate text-xs text-gray-500 dark:text-gray-400')}>
                                    {[contact.email, contact.phone]
                                        .filter((v) => !!v && `${v}`.trim().length > 0)
                                        .join(' • ')}
                                </span>
                                <span className={clsx('block truncate text-xs text-gray-500 dark:text-gray-400')}>
                                    {[contact.title, contact.position]
                                        .filter((v) => !!v && `${v}`.trim().length > 0)
                                        .join(' • ')}
                                </span>
                            </div>
                        </li>
                    )}
                </ComboboxOption>
            ))}
        </>
    )
}

export default CompanyContactAutocompleteItemsList
